import { injectable } from 'inversify'
import { isEqual } from 'lodash-es'
import type { FilterStrategyInterface } from '@/services/tables/filters/strategies/types/filterStrategyInterface'
import type { FilterField } from '@/services/tables/filters/fields/filterField'
import type { FilterOperationValue } from '@/services/types'
import type { PaginationUrlFilterScalarValueType, ParsedFilterType } from '@/services/tables/types'
import type { FilterStrategyGetPopulateDataResult } from '@/services/tables/filters/strategies/types/filterStrategyGetPopulateDataResult'
import type { ToFilterQueryPart } from '@/services/tables/filters/types'
import { isBoolFilterOperation } from '@/services/types'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'
import type { SelectOption } from '@/services/forms/types'

@injectable()
export class BaseFilterStrategy implements FilterStrategyInterface {
  public toQueryPart(filter: FilterField): Partial<FilterOperationValue> {
    return this.fieldDataToQueryPart({
      operation: filter.getCurrentOperation(),
      relationField: filter.getRelationField(),
      value: filter.getApiValue(),
    })
  }

  public getPopulateData(
    filter: FilterField,
    payload: ParsedFilterType[],
    selectedItems?: SelectOption[],
  ): FilterStrategyGetPopulateDataResult {
    const filteredPayload = payload.filter((item) => filter.matchesPayloadEntry(item))

    const appliedPayload =
      filteredPayload.find((item) => {
        if (!!item.value && Array.isArray(item.value)) {
          return item.value.length !== 0
        }

        if (isBoolFilterOperation(item.operation)) {
          return typeof item.value === 'boolean'
        }

        return !!item.value
      }) ?? filteredPayload[0]

    if (!appliedPayload) {
      return null
    }

    // applyFilterValue -> selectedItemsValue = [selected option] in repository
    // URL updates -> populateFromUrl with selectedItems = []
    // getPopulateData -> filter.selectedItemsValue = [selected option]
    // hasNewSelectedItems = false, -> currentSelectedItemsValue = [selected option]
    // When Clear is used, selectedItemsValue is already empty in the filter, so this preserves [].
    const currentSelectedItemsValue = filter.selectedItemsValue ?? []
    const currentInnerSelectedItemsValue = filter.innerSelectedItemsValue ?? []
    const hasNewSelectedItems = selectedItems && selectedItems.length > 0
    // The url carries values without labels, so after a restore the filter has nothing to render
    // the applied value with. For filters with static options the labels are already known and the
    // selected items are rebuilt from them. Filters with loadable options resolve labels themselves.
    const optionsSelectedItems = this.getSelectedItemsFromOptions(filter, appliedPayload.value)

    return {
      isApplied: true,
      operation: appliedPayload.operation,
      innerOperation: appliedPayload.operation,
      value: appliedPayload.value,
      innerValue: appliedPayload.value,
      selectedItemsValue: hasNewSelectedItems
        ? [...selectedItems]
        : this.preferKnownSelectedItems(currentSelectedItemsValue, optionsSelectedItems),
      innerSelectedItemsValue: hasNewSelectedItems
        ? [...selectedItems]
        : this.preferKnownSelectedItems(currentInnerSelectedItemsValue, optionsSelectedItems),
    }
  }

  public fieldDataToQueryPart(data: ToFilterQueryPart): PaginationUrlFilterScalarValueType {
    const value = isBoolFilterOperation(data.operation) ? coersceBoolean(data.value, '1', '0') : data.value

    if (data.relationField) {
      return { [data.relationField]: { [data.operation]: value } }
    }

    return { [data.operation]: value }
  }

  public checkFilterValue(filter: FilterField, valueToCheck: unknown): boolean {
    const value = filter.getApiValue()
    if (filter.operation === 'isEmpty') {
      return !valueToCheck && value === valueToCheck
    }
    if (filter.operation === 'isNull') {
      return valueToCheck === null && value === valueToCheck
    }
    if (filter.operation === 'in') {
      if (Array.isArray(value) && !Array.isArray(valueToCheck)) {
        return value.some((item) => isEqual(item, valueToCheck))
      }
      if (Array.isArray(value) && Array.isArray(valueToCheck)) {
        return value.some((item) => valueToCheck.some((checkItem) => isEqual(item, checkItem)))
      }
      return isEqual(value, valueToCheck)
    }
    if (filter.operation === 'eq') {
      return isEqual(value, valueToCheck)
    }
    if (filter.operation === 'lt') {
      return (valueToCheck as any) < (value as any)
    }
    if (filter.operation === 'lte') {
      return (valueToCheck as any) <= (value as any)
    }
    if (filter.operation === 'gt') {
      return (valueToCheck as any) > (value as any)
    }
    if (filter.operation === 'gte') {
      return (valueToCheck as any) >= (value as any)
    }
    return false
  }

  private preferKnownSelectedItems(currentItems: SelectOption[], optionsItems: SelectOption[]): SelectOption[] {
    return currentItems.length > 0 ? [...currentItems] : optionsItems
  }

  private getSelectedItemsFromOptions(filter: FilterField, value: unknown): SelectOption[] {
    // Loadable options are a fetch function, there is nothing to resolve labels from
    if (!Array.isArray(filter.options)) {
      return []
    }

    const values = Array.isArray(value) ? value : [value]

    return filter.options.filter((option) => values.some((item) => isEqual(item, option.value)))
  }
}
